import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Snackbar, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckIcon from "@mui/icons-material/Check";
import { FichaContenedor, FilaFicha } from "../../solicitudCheque/components/FichaSolicitud";
import { buscarClientesOC, buscarProveedoresOC, crearOrden, editarOrden, mensajeError, subirArchivosOC } from "../services/ordenCompraService";

const hoy = () => new Date().toISOString().slice(0, 10);

const vacio = (cat) => ({
  fecha: hoy(), proveedor: "", cliente: "", costo_embarque: "", cuotas: "1", moneda: "1", cambio: cat?.cambio ? String(cat.cambio) : "", embarcado: "", no_guia: "", observacion: "",
});

// Registrar o modificar la cabecera de una orden de compra. Al registrar se pueden adjuntar las proformas.
export default function OrdenFormDialog({ open, orden, catalogos, onClose, onGuardada }) {
  const [form, setForm] = useState(() => vacio(catalogos));
  const [archivos, setArchivos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const inputArchivos = useRef(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setArchivos([]);
    if (orden) {
      setForm({
        fecha: orden.fecha, proveedor: orden.proveedor, cliente: orden.cliente, costo_embarque: orden.costo_embarque, cuotas: orden.cuotas, moneda: String(orden.moneda),
        cambio: String(orden.tipo_cambio), embarcado: orden.embarcado, no_guia: orden.no_guia, observacion: orden.observacion,
      });
    } else {
      setForm(vacio(catalogos));
    }
  }, [open, orden, catalogos]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const buscarProveedor = async (texto) => {
    try { const { data } = await buscarProveedoresOC(texto); setProveedores(Array.isArray(data) ? data : []); } catch { setProveedores([]); }
  };
  const buscarCliente = async (texto) => {
    if (texto.trim().length < 2) return setClientes([]);
    try { const { data } = await buscarClientesOC(texto); setClientes(Array.isArray(data) ? data : []); } catch { setClientes([]); }
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.proveedor.trim()) return setError("Indica el proveedor.");
    if (!form.embarcado.trim()) return setError("Indica el embarcador.");
    if (!(Number(String(form.cambio).replace(",", ".")) > 0)) return setError("El tipo de cambio debe ser mayor a 0.");
    setGuardando(true);
    setError("");
    try {
      let id = orden?.id;
      if (orden) {
        await editarOrden(orden.id, form);
      } else {
        const { data } = await crearOrden(form);
        id = data.id;
      }
      let aviso = "";
      if (archivos.length) {
        try { await subirArchivosOC(id, archivos); } catch (e2) { aviso = `La orden se guardó, pero no se pudieron subir los archivos: ${mensajeError(e2, "error desconocido")}`; }
      }
      onGuardada({ id, nueva: !orden, aviso });
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar la orden."));
    } finally {
      setGuardando(false);
    }
  };

  const sim = form.moneda === "1" ? "$us" : "Bs";

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth component="form" onSubmit={guardar} scroll="paper">
      <DialogTitle sx={{ fontSize: 18, fontWeight: 800, py: 1.25 }}>{orden ? `Modificar orden de compra N.º ${orden.id}` : "Registrar orden de compra"}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.default", p: 1.5 }}>
        <FichaContenedor>
          <FilaFicha campos={[
            { etiqueta: "Fecha *", control: <TextField fullWidth size="small" type="date" hiddenLabel value={form.fecha} onChange={set("fecha")} slotProps={{ htmlInput: { "aria-label": "Fecha" } }} /> },
            { etiqueta: "Moneda", control: (
              <ToggleButtonGroup
                exclusive fullWidth size="small" color="primary" value={form.moneda} onChange={(_, v) => v && setForm((f) => ({ ...f, moneda: v }))}
                sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, py: 0.5, gap: 0.5 }, "&& .Mui-selected, && .Mui-selected:hover": { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 800 } }}
              >
                <ToggleButton value="0">{form.moneda === "0" && <CheckIcon sx={{ fontSize: 16 }} />}Bolivianos</ToggleButton>
                <ToggleButton value="1">{form.moneda === "1" && <CheckIcon sx={{ fontSize: 16 }} />}Dólares</ToggleButton>
              </ToggleButtonGroup>
            ) },
          ]}
          />
          <FilaFicha campos={[{ etiqueta: "Proveedor *", control: (
            <Autocomplete
              freeSolo fullWidth size="small" options={proveedores} value={form.proveedor} inputValue={form.proveedor} filterOptions={(x) => x}
              onFocus={() => buscarProveedor(form.proveedor)} onInputChange={(_, v) => { setForm((f) => ({ ...f, proveedor: v })); buscarProveedor(v); }}
              renderInput={(p) => <TextField {...p} hiddenLabel slotProps={{ htmlInput: { ...p.inputProps, maxLength: 50, "aria-label": "Proveedor" } }} />}
            />
          ) }]}
          />
          <FilaFicha campos={[{ etiqueta: "Cliente", control: (
            <Autocomplete
              freeSolo fullWidth size="small" options={clientes} value={form.cliente} inputValue={form.cliente} filterOptions={(x) => x}
              onInputChange={(_, v) => { setForm((f) => ({ ...f, cliente: v })); buscarCliente(v); }}
              renderInput={(p) => <TextField {...p} hiddenLabel placeholder="Opcional: si la compra es para un cliente" slotProps={{ htmlInput: { ...p.inputProps, "aria-label": "Cliente" } }} />}
            />
          ) }]}
          />
          <FilaFicha campos={[
            { etiqueta: "Tipo de cambio *", control: <TextField fullWidth size="small" hiddenLabel value={form.cambio} onChange={set("cambio")} slotProps={{ htmlInput: { inputMode: "decimal", "aria-label": "Tipo de cambio" } }} /> },
            { etiqueta: "N.º de cuotas", control: <TextField fullWidth size="small" hiddenLabel value={form.cuotas} onChange={set("cuotas")} slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 50, "aria-label": "Cantidad de cuotas" } }} /> },
          ]}
          />
          <FilaFicha campos={[
            { etiqueta: "Embarcador *", control: (
              <Autocomplete
                freeSolo fullWidth size="small" options={catalogos?.embarcados || []} value={form.embarcado} inputValue={form.embarcado}
                onInputChange={(_, v) => setForm((f) => ({ ...f, embarcado: v }))}
                renderInput={(p) => <TextField {...p} hiddenLabel slotProps={{ htmlInput: { ...p.inputProps, maxLength: 50, "aria-label": "Embarcador" } }} />}
              />
            ) },
            { etiqueta: "N.º de guía", control: <TextField fullWidth size="small" hiddenLabel value={form.no_guia} onChange={set("no_guia")} slotProps={{ htmlInput: { maxLength: 50, "aria-label": "Número de guía" } }} /> },
          ]}
          />
          <FilaFicha campos={[{ etiqueta: "Costo embarque", control: (
            <TextField
              fullWidth size="small" hiddenLabel value={form.costo_embarque} onChange={set("costo_embarque")} placeholder="0.00"
              slotProps={{ input: { startAdornment: <InputAdornment position="start">{sim}</InputAdornment> }, htmlInput: { inputMode: "decimal", maxLength: 50, "aria-label": "Costo de embarque" } }}
            />
          ) }]}
          />
          <FilaFicha campos={[{ etiqueta: "Observación", alto: true, control: <TextField fullWidth size="small" hiddenLabel multiline minRows={2} value={form.observacion} onChange={set("observacion")} slotProps={{ htmlInput: { "aria-label": "Observación" } }} /> }]} />
          {!orden && (
            <FilaFicha campos={[{ etiqueta: "Proforma", control: (
              <Box>
                <input ref={inputArchivos} type="file" multiple style={{ display: "none" }} onChange={(e) => { setArchivos((a) => [...a, ...Array.from(e.target.files)].slice(0, 10)); e.target.value = ""; }} />
                <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} sx={{ textTransform: "none" }} onClick={() => inputArchivos.current?.click()}>Adjuntar proforma</Button>
                <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "text.secondary" }}>Opcional · hasta 10 archivos</Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: archivos.length ? 1 : 0 }}>
                  {archivos.map((f, i) => <Chip key={`${f.name}${i}`} size="small" label={f.name} onDelete={() => setArchivos((a) => a.filter((_, j) => j !== i))} />)}
                </Box>
              </Box>
            ) }]}
            />
          )}
        </FichaContenedor>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={guardando} sx={{ textTransform: "none", fontWeight: 700 }}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : orden ? "Guardar cambios" : "Registrar orden"}
        </Button>
      </DialogActions>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={(_, m) => m !== "clickaway" && setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
    </Dialog>
  );
}
